<template>
  <FreePageLayout>
    <template #header>
      <FreeHeader class="p-4" is-centered>
        <template #leftAction>
          <FreeBackButton @click="handleBackClick" />
        </template>
        <template #default>
          <h2
            class="text-text-neutral-primary text-center text-lg font-semibold"
          >
            {{
              directFlightsStore.isOfferFromDirectFlights
                ? $t('search.directFlights.title')
                : isMultimodalOffer
                  ? $t('details.routeTitle')
                  : $t('details.title')
            }}
          </h2>
        </template>
        <template #rightAction>
          <div class="flex items-center gap-2">
            <SharePath
              :title="shareTitle"
              :path-for-deeplink="sharePathWithHash"
            />
            <FavoriteButton
              v-if="!isMultiTripOffer"
              :key="activeOffer?.id"
              :is-active="isOfferFavorite"
              :disabled="!isAuthorized"
              variant="icon"
              @toggle="toggleOfferFavorite"
            />
          </div>
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <section v-if="activeOffer" class="mb-28 space-y-4">
        <SaleDetailsBanner
          v-if="isSaleBannerVisible"
          class="relative z-50"
          :active-offer="activeOffer"
        />
        <DetailsWarningBanner
          v-if="isDetailsWarningVisible"
          :description="detailsWarningDescription"
        />
        <CharterFlightInfoContainer v-if="isFunSunOffer" />

        <AirlinesFilter
          v-if="showAirlinesFilter"
          class="-mx-4"
          :airlines="directFlightsStore.airlines"
          :selected-airline="directFlightsStore.selectedAirline"
          @onAirlineClick="handleAirlineClick"
        />
        <div class="space-y-4">
          <SmartRoute
            v-if="isSmartRoute"
            @onLabelClick="bottomSheet.show(smartRouteBottomSheetName)"
          />
          <BaggageWarning v-if="isDifferentBaggage" />
          <div
            v-for="(flight, idx) in activeOffer.flights"
            :key="idx"
            class="space-y-4"
          >
            <DirectionTimeSection
              :show-direct-flights="showDirectFlightsUI"
              :time-items="getDirectionTimeItems(idx)"
              :is-round-trip="!!searchParams?.RT"
              :direction-index="idx"
              @onTimeClick="handleTimeClick($event, idx)"
            />
            <FlightDetailsCard
              source-page="details"
              :flight="flight"
              :flight-idx="idx"
              :dict="dict"
              :city-mismatches="cityMismatches || null"
              @onFaqItemClick="showFaqBottomSheet"
            />
          </div>
          <TariffImprovement />
        </div>
      </section>
      <SmartRouteBottomSheet :name="smartRouteBottomSheetName" />
      <FaqBottomSheet :name="faqBottomSheetName" />
      <ChangeTimeModal
        :modal-name="changeTimeModalName"
        @onAgree="onChangeTimeModalAgree"
      />
      <PriceAndButtonCard
        source-page="details"
        :price-amount="activeOffer?.price?.amount || 0"
        :button-title="$t(proceedButtonTitleKey)"
        @onButtonAction="proceedToBooking"
      />
      <FavoriteAddedAlert ref="favoriteAddedAlertRef" />
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import { computed, ref, useTemplateRef } from 'vue';
  import { storeToRefs } from 'pinia';
  import {
    FreeBackButton,
    FreeHeader,
    FreePageLayout,
    FaqBottomSheet,
    FlightDetailsCard,
    CharterFlightInfoContainer,
    useFirebaseStore,
    type flightEntity,
  } from 'freedom-shared';
  import { useRouter, useRoute } from 'vue-router';
  import {
    ticketDetails,
    controllerModel,
    hashCodeLib,
    providerClassLib,
  } from 'shared';
  import type { Dict } from '@aviata/platform-types';

  import {
    directFlightsLib,
    useDirectFlightsStore,
    type directFlightsTypes,
  } from '@/widgets/direct-flights';

  import { PriceAndButtonCard } from '@/features/price-and-button-card';
  import { SharePath } from '@/features/share';
  import {
    FavoriteAddedAlert,
    FavoriteButton,
    FAVORITE_WATCHLIST_KIND,
    useWatchlist,
  } from '@/features/favorites';
  import { isQuickBookingEligible } from '@/features/quick-booking/lib/eligibility';
  import {
    QUICK_BOOKING_VARIANT,
    useQuickBookingExperiment,
  } from '@/features/quick-booking/model/use-quick-booking-experiment';

  import { isSmartOffer } from '@/entities/offer';

  import { analyticsEventsLib, amplitudeEventsLib } from '@/shared/lib';
  import {
    amplitudeEventsConfig,
    type amplitudeEventsConfigTypes,
  } from '@/shared/config';

  import {
    ChangeTimeModal,
    TariffImprovement,
    SmartRoute,
    BaggageWarning,
    SmartRouteBottomSheet,
    AirlinesFilter,
    DirectionTimeSection,
    DetailsWarningBanner,
    SaleDetailsBanner,
  } from './ui';
  import { useTimeSelection, useDetailsWarningBanner } from './model';
  import type { TimeItem } from './types';
  import { getLinkTitle, getLinkPath } from './lib';

  const props = defineProps<{
    activeOffer?: flightEntity.types.MappedOffer;
    dict: Dict.Entity;
    resultId: string;
    hashCode?: string;
    allOffers?: flightEntity.types.MappedOffer[];
  }>();

  const emit = defineEmits<{
    onOfferSelect: [offer: flightEntity.types.MappedOffer];
  }>();

  const router = useRouter();
  const route = useRoute();

  const bottomSheet = controllerModel.useBottomSheet();
  const modal = controllerModel.useModal();
  const faqBottomSheetName = 'faq-bottom-sheet';
  const smartRouteBottomSheetName = 'smart-route-bottom-sheet';
  const changeTimeModalName = 'change-time-confirmation-modal';

  const searchParams = computed(() =>
    props.hashCode ? hashCodeLib.getSearchParams(props.hashCode) : null,
  );
  const activeOffer = ref(props.activeOffer);

  /** Multi-city search from the hash (3+ itineraries in query), not OW/RT. */
  const isMultiTripOffer = computed(() =>
    props.hashCode ? hashCodeLib.isMultiTripSearch(props.hashCode) : false,
  );

  const { serializedRemoteConfig } = storeToRefs(useFirebaseStore());

  const isSaleBannerVisible = computed(
    () => serializedRemoteConfig.value?.avfree_details_sale_banner === true,
  );

  const { favoriteHashes, toggleOffer, isAuthorized } = useWatchlist();

  const favoriteAddedAlertRef = useTemplateRef<
    InstanceType<typeof FavoriteAddedAlert>
  >('favoriteAddedAlertRef');

  const isOfferFavorite = computed(() =>
    favoriteHashes.value.has(
      (activeOffer.value?.$meta as { hash?: string })?.hash ?? '',
    ),
  );

  const toggleOfferFavorite = async () => {
    const hash = (activeOffer.value?.$meta as { hash?: string })?.hash;
    if (!hash || !activeOffer.value) return;
    const result = await toggleOffer(
      hash,
      props.hashCode ?? '',
      activeOffer.value.price.amount,
      activeOffer.value.price.currency,
    );
    if (result === 'added') {
      amplitudeEventsLib.addToFavorites('details_page');
      favoriteAddedAlertRef.value?.show(FAVORITE_WATCHLIST_KIND.TICKET);
    } else if (result === 'removed') {
      amplitudeEventsLib.removeFromFavorites('details_page');
    } else if (result === 'limit_reached') {
      favoriteAddedAlertRef.value?.showLimitError();
    }
  };
  const initialAirlineIata =
    props.activeOffer?.flights[0]?.segments[0]?.airline;
  const initialDepartureTime =
    props.activeOffer?.flights[0]?.segments[0]?.dep?.time;
  const initialReturnTime =
    props.activeOffer?.flights[1]?.segments[0]?.dep?.time;

  const shareTitle = computed(() =>
    getLinkTitle(activeOffer.value, searchParams.value),
  );

  const sharePathWithHash = computed(() =>
    getLinkPath(activeOffer.value, route.params),
  );

  const directFlightsStore = useDirectFlightsStore();
  const isRoundTrip = computed(() => searchParams.value?.RT ?? false);

  const quickBookingExperiment = useQuickBookingExperiment();

  /** True when proceedToBooking will route the user into QuickBookingPage
   *  instead of FareFamilyPage. Both QB variants (AFTER_DETAILS, and the
   *  direct-flights detour of IMMEDIATE) take this branch. */
  const willRouteToQuickBooking = computed(() => {
    const offer = activeOffer.value;
    const sp = searchParams.value;
    const variant = quickBookingExperiment.variant.value;
    const isEligibleForQuickBooking = !!(
      offer &&
      sp &&
      isQuickBookingEligible(offer, sp)
    );
    return (
      isEligibleForQuickBooking &&
      (variant === QUICK_BOOKING_VARIANT.AFTER_DETAILS ||
        variant === QUICK_BOOKING_VARIANT.IMMEDIATE)
    );
  });

  /** Switches the "Continue" button copy when proceedToBooking would route
   *  into QuickBookingPage — the user skips the FareFamily picker and goes
   *  straight into a booking-style flow, so "Go to tariffs" no longer fits. */
  const proceedButtonTitleKey = computed(() =>
    willRouteToQuickBooking.value ? 'details.to-booking' : 'details.to-tariffs',
  );

  const backwardTimesMapping = computed(
    () => directFlightsStore.selectedGroup?.backwardTimesByForwardTime,
  );
  const forwardTimesMapping = computed(
    () => directFlightsStore.selectedGroup?.forwardTimesByBackwardTime,
  );

  const showDirectFlightsUI = computed(
    () =>
      directFlightsLib.shouldShowDirectFlights(
        activeOffer.value,
        directFlightsStore,
        isRoundTrip.value,
      ) && !isMultiTripOffer.value,
  );

  const showAirlinesFilter = computed(
    () =>
      showDirectFlightsUI.value && directFlightsStore.isOfferFromDirectFlights,
  );

  const {
    getDirectionTimeItems,
    handleDirectionTimeClick,
    applyDisabledTimeSelection,
  } = useTimeSelection(
    computed(() => directFlightsStore.filteredOffersByAirline),
    activeOffer,
    isRoundTrip,
    backwardTimesMapping,
    forwardTimesMapping,
  );

  const handleAirlineClick = (airline: directFlightsTypes.Airline) => {
    // Save current times before switching
    if (directFlightsStore.selectedAirline && activeOffer.value) {
      directFlightsStore.saveTimesForAirline(
        directFlightsStore.selectedAirline.iata,
        activeOffer.value,
      );
    }

    directFlightsStore.selectAirline(airline);

    // Restore times for new airline
    const storedTimes = directFlightsStore.getStoredTimesForAirline(
      airline.iata,
    );
    const targetOffer = directFlightsStore.selectedGroup
      ? directFlightsLib.findOfferByStoredTimes(
          directFlightsStore.selectedGroup,
          storedTimes,
          isRoundTrip.value,
        )
      : null;

    if (targetOffer) {
      activeOffer.value = targetOffer;
      emit('onOfferSelect', targetOffer);
    }
  };

  const handleOfferSelect = (offer: flightEntity.types.MappedOffer) => {
    activeOffer.value = offer;
    if (directFlightsStore.selectedAirline) {
      directFlightsStore.saveTimesForAirline(
        directFlightsStore.selectedAirline.iata,
        offer,
      );
    }
    emit('onOfferSelect', offer);
  };

  const isSmartRoute = computed(() =>
    activeOffer.value ? isSmartOffer(activeOffer.value) : false,
  );

  const pendingDisabledTimeSelection = ref<{
    item: TimeItem;
    flightIdx: number;
  } | null>(null);

  const handleTimeClick = (item: TimeItem, flightIdx: number) => {
    if (item.isDisabled) {
      modal.show(changeTimeModalName, { changingDirection: flightIdx });
      pendingDisabledTimeSelection.value = { item, flightIdx };
    } else {
      handleDirectionTimeClick(item, flightIdx, handleOfferSelect);
    }
  };

  const onChangeTimeModalAgree = () => {
    if (pendingDisabledTimeSelection.value) {
      const { item, flightIdx } = pendingDisabledTimeSelection.value;
      applyDisabledTimeSelection(item, flightIdx, handleOfferSelect);
      pendingDisabledTimeSelection.value = null;
    }
  };

  const isMultimodalOffer = computed(
    () =>
      !!activeOffer.value &&
      ticketDetails.lib.getOfferTransportKind(activeOffer.value) !== 'air',
  );

  const isDifferentBaggage = computed(() =>
    activeOffer.value
      ? ticketDetails.lib.isDifferentBaggages(activeOffer.value)
      : false,
  );
  const cityMismatches = computed(() => activeOffer.value?.$meta.city_mismatch);

  const {
    isVisible: isDetailsWarningVisible,
    description: detailsWarningDescription,
  } = useDetailsWarningBanner(activeOffer, props.dict);

  const isFunSunOffer = computed(() =>
    providerClassLib.isFunSun(activeOffer.value?.$meta.provider_class),
  );

  const showFaqBottomSheet = (faqItemKey: string) =>
    bottomSheet.show(faqBottomSheetName, { faqItemKey });

  const proceedToBooking = () => {
    if (activeOffer.value) {
      const source: amplitudeEventsConfigTypes.DetailsPageOpenSource =
        directFlightsStore.isOfferFromDirectFlights
          ? amplitudeEventsConfig.DETAILS_PAGE_OPEN_SOURCE.DIRECT_FLIGHTS
          : ((route.params
              .source as amplitudeEventsConfigTypes.DetailsPageOpenSource) ??
            'main_page');
      const index =
        props.allOffers?.findIndex(o => o.id === activeOffer.value?.id) ?? 0;

      analyticsEventsLib.detailsPageContinue({
        offer: activeOffer.value,
        index,
        source,
        dict: props.dict,
        searchQuery: props.hashCode ?? '',
        ...(showDirectFlightsUI.value && {
          another_time_choosen:
            activeOffer.value.flights[0]?.segments[0]?.dep?.time !==
              initialDepartureTime ||
            activeOffer.value.flights[1]?.segments[0]?.dep?.time !==
              initialReturnTime,
          another_airline_changed:
            activeOffer.value.flights[0]?.segments[0]?.airline !==
            initialAirlineIata,
        }),
      });
    }

    // QuickBooking assignment is logged on the ResultsPage click already,
    // so here we only act on the variant + eligibility check to pick the
    // next route. AFTER_DETAILS always lands on QuickBookingPage here;
    // IMMEDIATE only reaches OfferDetails via the direct-flights detour
    // and still wants to end on QuickBookingPage on continue.
    if (willRouteToQuickBooking.value && activeOffer.value) {
      router.push({
        name: 'QuickBookingPage',
        params: {
          resultId: props.resultId,
          offerId: activeOffer.value.id,
          source: route.params.source,
        },
        query: {
          back: route.query.back === 'chat' ? 'chat' : undefined,
        },
      });
      return;
    }

    router.push({
      name: 'FareFamilyPage',
      params: {
        resultId: props.resultId,
        offerId: activeOffer.value?.id,
        source: route.params.source,
      },
    });
  };

  const handleBackClick = () => {
    if (route.query.back === 'chat') {
      router.push({
        name: 'MainPage',
        query: {
          back: 'chat',
        },
      });
      return;
    }

    router.back();
  };
</script>
